import { useCallback } from 'react';

import {
  CRM_RequestType,
  isLeadIntent,
  LeadIntent,
  ResponsePreference,
  ServiceSegmentType,
} from '@mewa/types';
import {
  emailValidator,
  notEmptyValidator,
  stringLengthValidator,
} from '@mewa/validation';

import { useFormField } from './use-form-field';

export type FormDataState = ReturnType<typeof useFormData>;

export const useFormData = (formType: CRM_RequestType) => {
  const leadIntent = useFormField<LeadIntent | null>({
    initialValue: null,
    validation: notEmptyValidator(),
    validateOnChange: true,
  });

  const validateServiceSegment = useCallback(
    (s: ServiceSegmentType | null) =>
      (leadIntent.value !== LeadIntent.product &&
        leadIntent.value !== LeadIntent.consultation) ||
      formType !== CRM_RequestType.Consultation ||
      notEmptyValidator()(s),
    [leadIntent.value, formType],
  );

  const serviceSegment = useFormField<ServiceSegmentType | null>({
    initialValue: null,
    validation: validateServiceSegment,
    validateOnChange: true,
  });

  const firstName = useFormField<string | null>({
    initialValue: null,
    validation: stringLengthValidator(1, 40),
  });
  const lastName = useFormField<string | null>({
    initialValue: null,
    validation: stringLengthValidator(1, 40),
  });

  const validatePhone = useCallback(
    (s: string | null, ...args: unknown[]) => {
      let leadIntentValue = leadIntent.value;

      if (args[0] !== undefined && isLeadIntent(args[0])) {
        leadIntentValue = args[0];
      }

      return leadIntentValue !== LeadIntent.recall || notEmptyValidator()(s);
    },
    [leadIntent.value],
  );

  const phone = useFormField<string | null>({
    initialValue: null,
    validation: validatePhone,
  });

  const email = useFormField<string | null>({
    initialValue: null,
    validation: emailValidator(255),
  });

  const message = useFormField<string | null>({ initialValue: null });

  const responsePreference = useFormField<Record<ResponsePreference, boolean>>({
    initialValue: {
      [ResponsePreference.morning]: false,
      [ResponsePreference.afternoon]: false,
    },
  });

  const legal = useFormField<boolean | null>({
    initialValue: null,
    validation: (value) => !!value,
    validateOnChange: true,
  });

  const triggerValidation = useCallback(() => {
    firstName.triggerValidation();
    lastName.triggerValidation();
    email.triggerValidation();
    phone.triggerValidation();
    serviceSegment.triggerValidation();
    leadIntent.triggerValidation();
    legal.triggerValidation();
  }, [firstName, lastName, email, phone, serviceSegment, leadIntent, legal]);

  const isValid = useCallback(
    () =>
      firstName.checkValidation() &&
      lastName.checkValidation() &&
      email.checkValidation() &&
      phone.checkValidation() &&
      legal.checkValidation() &&
      leadIntent.checkValidation() &&
      serviceSegment.checkValidation(),
    [firstName, lastName, email, phone, legal, leadIntent, serviceSegment],
  );

  return {
    serviceSegment,
    leadIntent,
    firstName,
    lastName,
    phone,
    email,
    message,
    responsePreference,
    legal,

    isValid,
    triggerValidation,
  };
};
